import { useSearchParams, useNavigate } from "react-router-dom";
import { View, Text, XStack, YStack, Button } from "tamagui";
import {
  RiCheckLine,
  RiShieldCheckLine,
  RiTimeLine,
  RiInformationLine,
  RiCheckboxCircleLine,
  RiErrorWarningLine,
  RiCloseCircleLine,
  RiBankCardLine,
} from "react-icons/ri";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";
import { colors } from "@styles/colors";
import { Header, Fade, detectCard, RADIUS } from "./Common";

function InfoRow({ icon: Icon, iconC, label, value }: { icon: any; iconC: string; label: string; value: string }) {
  return (
    <XStack ai="center" gap="$2">
      <Icon size={12} color={iconC} />
      <Text fontSize={11} color={colors.gray200}>
        {label}
      </Text>
      <Text fontSize={12} fontWeight="700" color={colors.grayDark} ml="auto">
        {value}
      </Text>
    </XStack>
  );
}

export default function PaymentResult() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const status = searchParams.get("status"); // "success" or "error"
  const errorMsg = searchParams.get("msg") || "未知錯誤";
  const lastFour = searchParams.get("lastfour") || "";
  const cardType = searchParams.get("type") || "";

  const ct = detectCard(lastFour);
  const now = new Date().toLocaleString("zh-TW", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });

  const isSuccess = status === "success";

  return (
    <RadiusContentWrapper padding="0" space={0}>
      <YStack
        background={
          isSuccess
            ? `linear-gradient(135deg, ${colors.primary}, ${colors.primary}DD)`
            : `linear-gradient(135deg, ${colors.danger}, ${colors.danger}DD)`
        }
        pb="$5"
        px="$5"
        br="$5"
        borderBottomLeftRadius={35}
        borderBottomRightRadius={35}
        borderTopLeftRadius={0}
        borderTopRightRadius={0}
        position="relative"
        ov="hidden">
        <View position="absolute" right={-55} top={-55} width={190} height={190} br={95} bg="rgba(255,255,255,0.07)" pointerEvents="none" />
        <View h={30} />
        <YStack ai="center" position="relative">
          {isSuccess ? <RiCheckboxCircleLine color="#ffffffd8" size={50} /> : <RiCloseCircleLine color="#ffffffd8" size={50} />}
          <Text fontSize={22} fontWeight="800" color="#fff" mb="$1">
            {isSuccess ? "綁卡成功" : "綁定失敗"}
          </Text>
          <Text fontSize={14} color="rgba(255,255,255,0.88)" textAlign="center">
            {isSuccess ? "你的信用卡已透過 TapPay 安全綁定" : errorMsg}
          </Text>
        </YStack>
      </YStack>

      <YStack pt="$4" px="$4" ai="center">
        {isSuccess ? (
          <Fade i={1}>
            <YStack bg={colors.light} br={RADIUS} p="$5" mb="$3.5" w="100%" ai="center" gap="$3">
              <YStack>
                <XStack f={1} ai="center" gap="$4" mb="$3.5" pb="$3.5" bbw={1} bbc={colors.bgGray1} w="100%">
                  <View
                    w={58}
                    h={38}
                    br={9}
                    background={ct ? `linear-gradient(135deg,${ct.color},${ct.color}99)` : "linear-gradient(135deg,#1E3A5F,#2563EB)"}
                    ai="center"
                    jc="center">
                    <Text fontSize={11} fontWeight="800" color="#fff">
                      {ct?.name || cardType || "CARD"}
                    </Text>
                  </View>
                  <XStack ai="center" gap="$1" bg={colors.bgGreen} br={9} px="$2">
                    <RiCheckLine size={12} color={colors.green} />
                    <Text fontSize={10} fontWeight="700" color={colors.green}>
                      已驗證
                    </Text>
                  </XStack>
                </XStack>
                <YStack gap="$2.5">
                  <InfoRow icon={RiTimeLine} iconC={colors.primary} label="綁定時間" value={now} />
                  <InfoRow icon={RiShieldCheckLine} iconC={colors.amber} label="3DS 狀態" value="驗證通過" />
                  {lastFour && <InfoRow icon={RiBankCardLine} iconC={colors.gray200} label="卡號末四碼" value={lastFour} />}
                </YStack>
              </YStack>
            </YStack>
          </Fade>
        ) : (
          <Fade i={1}>
            <YStack bg={colors.light} br={RADIUS} p="$5" mb="$3.5" w="100%" ai="center" gap="$3">
              <RiErrorWarningLine size={40} color={colors.danger} />
              <Text fontSize={16} fontWeight="700" color={colors.grayDark}>
                綁定過程中發生錯誤
              </Text>
              <Text fontSize={13} color={colors.gray200} textAlign="center">
                請確認你的卡片資訊是否正確，或更換其他信用卡再試一次。
              </Text>
            </YStack>
          </Fade>
        )}

        {isSuccess && (
          <Fade i={2}>
            <XStack bg={colors.primaryActive} br={RADIUS} mx="$4" p="$3" mb="$3.5" ai="flex-start" gap="$2.5">
              <RiInformationLine size={16} color={colors.primary} />
              <Text f={1} fontSize={11} color={colors.primary}>
                卡號已由 TapPay 加密儲存，本平台不保存完整卡號。如需刪除，可在「付款方式」中管理。
              </Text>
            </XStack>
          </Fade>
        )}
      </YStack>

      <XStack p="$4" jc="center" gap="$2.5">
        <Button onPress={() => navigate("/account/paymentMethod")} f={1} bg={isSuccess ? colors.bg : colors.primary} bw={0} br={12} p="$3" h="auto">
          <Text fontSize={14} fontWeight="600" color={isSuccess ? colors.gray : "#fff"}>
            {isSuccess ? "返回列表" : "重新嘗試"}
          </Text>
        </Button>
        {isSuccess && (
          <Button
            onPress={() => navigate("/account/paymentMethod")}
            f={2}
            className="primary-bg-gradient"
            bw={0}
            br={12}
            p="$3"
            h="auto"
            style={{ boxShadow: "0 4px 14px #2563EB40" }}>
            <Text fontSize={14} fontWeight="700" color="#fff">
              立即啟用到帳戶
            </Text>
          </Button>
        )}
      </XStack>
    </RadiusContentWrapper>
  );
}
